import { useEffect, useState } from "react";
import { DataRequestStateEnum, DataRequestType } from "@/core/types/dataRequestsTypes";
import useDataRequestState from "@/core/dataProviders/hooks/useDataRequestState";
import useDataRequestOrder from "@/core/dataProviders/hooks/useDataRequestOrder";
import { useAppSelector } from "@/core/store/reduxHooks";
import { AuthorType } from "@/core/types/contentTypes";
import { AuthorSlugMapSelector, AuthorsSelector } from "@/core/store/bucket/BucketSlice";
import useLangSelector from "@/core/dataProviders/hooks/useLangSelector";
import getFullContent from "@/core/dataProviders/utils/getFullContent";
import isNewOrderNeeded from "@/core/dataProviders/utils/isNewOrderNeeded";

/**
 * data provider for front-end pages based on available options for given data
 * - checks if order exists / creates orders / returns order status
 * - gathers and returns requested data
 * */

export default function useAuthorBySlugProvider(
  sign: { slug: string } | null
): [AuthorType | null, DataRequestStateEnum] {
  const orderData = useDataRequestOrder();
  const authors = useLangSelector(useAppSelector(AuthorsSelector));
  const slugToAuthorId = useLangSelector(useAppSelector(AuthorSlugMapSelector));

  const slug = sign ? sign.slug : null;

  const [currentAuthor, setCurrentAuthor] = useState<AuthorType | null>(
    () => sign && getFullContent(sign.slug, authors, slugToAuthorId)
  );
  const [currentReqState, , setReqSign] = useDataRequestState({
    type: DataRequestType.AUTHOR_BY_SLUG,
    sign: sign,
  });

  useEffect(() => {
    setCurrentAuthor(sign && getFullContent(sign.slug, authors, slugToAuthorId));
    setReqSign(sign);
  }, [slug]);

  useEffect(() => {
    setCurrentAuthor(sign && getFullContent(sign.slug, authors, slugToAuthorId));
  }, [slugToAuthorId, authors]);

  useEffect(() => {
    if (!sign || isNewOrderNeeded(!!currentAuthor, currentReqState)) {
      return;
    }

    orderData({
      type: DataRequestType.AUTHOR_BY_SLUG,
      sign: sign,
    });
  }, [currentReqState, orderData]);

  return [currentAuthor, currentReqState];
}
